import { UserManager, WebStorageStateStore, User } from "oidc-client-ts";
import { getConfig } from "./config";

const SESSION_MAX_MS = 8 * 60 * 60 * 1000;
const SESSION_START_KEY = "tagging.sessionStart";

let _manager: UserManager | null = null;

export function getUserManager(): UserManager {
  if (_manager) return _manager;
  const cfg = getConfig();
  _manager = new UserManager({
    authority: `https://cognito-idp.${cfg.region}.amazonaws.com/${cfg.userPoolId}`,
    client_id: cfg.userPoolClientId,
    redirect_uri: `${window.location.origin}/`,
    post_logout_redirect_uri: `${window.location.origin}/`,
    response_type: "code",
    scope: "openid email profile",
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    automaticSilentRenew: true,
  });
  return _manager;
}

export async function login(): Promise<void> {
  await getUserManager().signinRedirect();
}

export async function completeLogin(): Promise<User> {
  const user = await getUserManager().signinRedirectCallback();
  sessionStorage.setItem(SESSION_START_KEY, String(Date.now()));
  return user;
}

export async function logout(): Promise<void> {
  sessionStorage.removeItem(SESSION_START_KEY);
  const cfg = getConfig();
  // Cognito logout endpoint must be used for Hosted UI sessions
  const logoutUrl = `${cfg.cognitoDomain}/logout?client_id=${encodeURIComponent(
    cfg.userPoolClientId,
  )}&logout_uri=${encodeURIComponent(window.location.origin + "/")}`;
  await getUserManager().removeUser();
  window.location.href = logoutUrl;
}

export async function getUser(): Promise<User | null> {
  const user = await getUserManager().getUser();
  if (!user) return null;
  if (isSessionExpired()) {
    await logout();
    return null;
  }
  return user;
}

export function isSessionExpired(): boolean {
  const start = Number(sessionStorage.getItem(SESSION_START_KEY) || 0);
  if (!start) return false;
  return Date.now() - start > SESSION_MAX_MS;
}

export async function getAccessToken(): Promise<string> {
  const user = await getUser();
  if (!user) throw new Error("not authenticated");
  // API Gateway CognitoUserPoolsAuthorizer validates the aud claim which is in the ID token
  return user.id_token!;
}
